export const emptyScooterFeatureCollection = {
  type: "FeatureCollection",
  features: [],
};

export const micromobilityMinVisibleZoom = 13;
export const micromobilityGroupDisplayRadiusMeters = 20;

const relativeMinuteFormatter = new Intl.RelativeTimeFormat("en-AU", {
  numeric: "always",
  style: "short",
});

const formatRelativeMinutes = (seconds) =>
  relativeMinuteFormatter
    .format(Math.round(seconds / 60), "minute")
    .replace(/^in\s+/i, "");

export const formatUpdatedLabel = (dateValue) => {
  const lastReportedMs = Date.parse(dateValue);

  if (!Number.isFinite(lastReportedMs)) {
    return null;
  }

  const seconds = (lastReportedMs - Date.now()) / 1000;
  const minutes = Math.round(seconds / 60);

  if (minutes === 0) {
    return "Updated just now";
  }

  return `Updated ${formatRelativeMinutes(seconds)}`;
};

export function getMicromobilityMarkerImageId(
  item,
  { active = false, group = false } = {},
) {
  const isJolt = String(item?.provider || "").toLowerCase() === "jolt";
  const isBicycle = item?.formFactor === "bicycle";

  if (group) {
    if (isJolt) {
      return "micromobility-jolt-scooter-group";
    }

    return isBicycle
      ? "micromobility-flamingo-bike-group"
      : "micromobility-flamingo-scooter-group";
  }

  if (isJolt) {
    return active
      ? "micromobility-jolt-scooter-active"
      : "micromobility-jolt-scooter";
  }

  if (isBicycle) {
    return active
      ? "micromobility-flamingo-bike-active"
      : "micromobility-flamingo-bike";
  }

  return active
    ? "micromobility-flamingo-scooter-active"
    : "micromobility-flamingo-scooter";
}

function getMicromobilityGroupMarkerImageId(item, count) {
  return `${getMicromobilityMarkerImageId(item, { group: true })}-${count}`;
}

const mixedHubMarkerOffsetMeters = 2;

function getCoordinateKey(vehicle) {
  const lat = Number(vehicle?.lat);
  const lon = Number(vehicle?.lon);

  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    return null;
  }

  return `${lat.toFixed(6)},${lon.toFixed(6)}`;
}

function isHubRecord(vehicle) {
  return (
    Boolean(vehicle?.stationName) ||
    String(vehicle?.id || "").startsWith("station:")
  );
}

function getMixedHubOffset(vehicle, groupedVehiclesByCoordinate) {
  if (!isHubRecord(vehicle)) {
    return { lat: Number(vehicle.lat), lon: Number(vehicle.lon) };
  }

  const key = getCoordinateKey(vehicle);
  const colocatedVehicles = key
    ? groupedVehiclesByCoordinate.get(key) || []
    : [];
  const formFactors = new Set(
    colocatedVehicles.map((candidate) => candidate.formFactor).filter(Boolean),
  );

  if (formFactors.size < 2) {
    return { lat: Number(vehicle.lat), lon: Number(vehicle.lon) };
  }

  const sortedVehicles = [...colocatedVehicles].sort((a, b) =>
    String(a.id).localeCompare(String(b.id)),
  );
  const offsetIndex = sortedVehicles.findIndex(
    (candidate) => candidate.id === vehicle.id,
  );

  if (offsetIndex < 0) {
    return { lat: Number(vehicle.lat), lon: Number(vehicle.lon) };
  }

  const angle =
    sortedVehicles.length === 2
      ? offsetIndex === 0
        ? Math.PI
        : 0
      : (offsetIndex / sortedVehicles.length) * Math.PI * 2;
  const metersPerDegreeLatitude = 111320;
  const metersPerDegreeLongitude =
    metersPerDegreeLatitude * Math.cos((Number(vehicle.lat) * Math.PI) / 180);

  return {
    lat:
      Number(vehicle.lat) +
      (Math.sin(angle) * mixedHubMarkerOffsetMeters) / metersPerDegreeLatitude,
    lon:
      Number(vehicle.lon) +
      (Math.cos(angle) * mixedHubMarkerOffsetMeters) /
        Math.max(metersPerDegreeLongitude, 1),
  };
}

function groupHubVehiclesByCoordinate(vehicles) {
  const groups = new Map();

  for (const vehicle of vehicles) {
    if (!isHubRecord(vehicle)) {
      continue;
    }

    const key = getCoordinateKey(vehicle);

    if (!key) {
      continue;
    }

    groups.set(key, [...(groups.get(key) || []), vehicle]);
  }

  return groups;
}

export const buildScooterFeatureCollection = (vehicleRecords) => {
  const vehicles = Object.values(vehicleRecords);
  const hubVehiclesByCoordinate = groupHubVehiclesByCoordinate(vehicles);

  return {
    type: "FeatureCollection",
    features: vehicles.map((vehicle) => {
      const vehicleCount = Number(vehicle.count) || 1;
      const isGrouped = vehicleCount > 1;
      const coordinates = getMixedHubOffset(vehicle, hubVehiclesByCoordinate);

      return {
        type: "Feature",
        properties: {
          id: vehicle.id,
          vehicleId: vehicle.id,
          provider: vehicle.provider,
          formFactor: vehicle.formFactor,
          markerImageId: isGrouped
            ? getMicromobilityGroupMarkerImageId(vehicle, vehicleCount)
            : getMicromobilityMarkerImageId(vehicle),
          activeMarkerImageId: getMicromobilityMarkerImageId(vehicle, {
            active: true,
          }),
          rangeMeters: vehicle.rangeMeters,
          lastReported: vehicle.lastReported,
          rentalUri: vehicle.rentalUri,
          stationName: vehicle.stationName,
          count: vehicle.count,
        },
        geometry: {
          type: "Point",
          coordinates: [coordinates.lon, coordinates.lat],
        },
      };
    }),
  };
};

export const getFeatureCoordinates = (feature) => ({
  lng: feature.geometry.coordinates[0],
  lat: feature.geometry.coordinates[1],
});

export const getFeatureByVehicleId = (featureCollection, vehicleId) =>
  featureCollection?.features?.find(
    (candidate) => candidate?.properties?.vehicleId === vehicleId,
  );

export const getBaseMapQueryFrom = (query = {}) => {
  const { stop, vehicle, scooter, lat, lng, pin, name, ...restQuery } = query;
  return restQuery;
};

export const buildSelectedScooterItem = (feature) => {
  const { lng, lat } = getFeatureCoordinates(feature);

  return {
    type: "scooter",
    id: feature.properties.vehicleId,
    provider: feature.properties.provider,
    formFactor: feature.properties.formFactor,
    markerImageId: feature.properties.markerImageId,
    rangeMeters: feature.properties.rangeMeters,
    lastReported: feature.properties.lastReported,
    rentalUri: feature.properties.rentalUri,
    stationName: feature.properties.stationName,
    count: feature.properties.count,
    lng,
    lat,
    selected: true,
  };
};

export const getClickedMapFeature = ({
  features,
  selectedItem,
  stopLayerId,
}) => {
  if (selectedItem?.type === "vehicle") {
    const stopFeature = features.find(
      (feature) => feature.layer?.id === stopLayerId,
    );

    if (stopFeature) {
      return stopFeature;
    }
  }

  return features[0];
};

export const getMicromobilityAccentColor = (markerImageId) => {
  if (String(markerImageId || "").startsWith("micromobility-jolt-scooter")) {
    return "#000000";
  }

  switch (markerImageId) {
    case "micromobility-flamingo-bike":
    case "micromobility-flamingo-scooter":
    case "micromobility-flamingo-bike-group":
    case "micromobility-flamingo-scooter-group":
      return "#FF206E";
    default:
      return "#FF206E";
  }
};

const pluraliseMicromobilityTitle = (title) =>
  String(title || "").endsWith("s") ? title : `${title}s`;

export const getMicromobilityPluralNoun = (item) =>
  item?.formFactor === "bicycle" ? "bikes" : "scooters";

export const formatMicromobilityStationName = (stationName) =>
  /\b(parking|hub|station)\b/i.test(stationName)
    ? stationName
    : `${stationName} parking hub`;

export const getMicromobilityTitle = (item) => {
  const provider = item?.provider || "Shared";
  const title =
    item?.formFactor === "bicycle"
      ? `${provider} Electric Bike`
      : `${provider} Electric Scooter`;

  return Number(item?.count) > 1
    ? pluraliseMicromobilityTitle(title)
    : title;
};
